import { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import { WebhookConfiguration } from './components/WebhookConfiguration';
import { WebhookDeliveryHistory } from './components/WebhookDeliveryHistory';
import { WebhookForm } from './components/WebhookForm';
import { DeleteWebhookScheme } from './PopUpSchemes/DeleteWebhookScheme';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { formatDateTime } from 'services/utils';
import { useApplicationContext } from 'contexts/ApplicationContext';
import Button from 'uikit/Button/Button';
import { PopUp } from 'uikit/PopUp/PopUp';
import './WebhookTab.scss';

const WEBHOOK_LIST_TABLE_HEADER = ['webhookUrl', 'direction', 'createdAt', 'actions'];

const WebhookTab = ({
  customerNumber,
  isLoading,
  isInitialized,
  getCurrentCustomerWebhookUrlList,
  createNewCustomerWebhookUrl,
  updateCustomerWebhookUrl,
  deleteCustomerWebhookUrl,
  testWebhookUrl,
  updateWebhookConfiguration,
  customerWebhookUrlList,
  directApiCallToken,
  generateDirectApiCallToken,
  revokeDirectApiCallToken,
  checkDirectApiCallToken,
  isDirectApiCallTokenExist
}) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();

  const webhookList = useMemo(() => customerWebhookUrlList || [], [customerWebhookUrlList]);
  const webhookLimit = constants?.webhookLimitPerAccount;
  // limit unknown (older backend) -> never block adding
  const isLimitReached = Number.isInteger(webhookLimit) && webhookList.length >= webhookLimit;

  const [selectedWebhookId, setSelectedWebhookId] = useState(null);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState(null);
  // URL of a webhook created a moment ago: selected as soon as it shows up in the reloaded list
  const [pendingSelectUrl, setPendingSelectUrl] = useState(null);
  // bumped after a test send so the delivery history shows it
  const [historyReloadToken, setHistoryReloadToken] = useState(0);

  const selectedWebhook = webhookList.find((webhook) => webhook.id === selectedWebhookId) || null;

  useEffect(() => {
    getCurrentCustomerWebhookUrlList();
    checkDirectApiCallToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drop the selection when the selected webhook disappears from the list (deleted elsewhere / reloaded)
  useEffect(() => {
    if (selectedWebhookId && !webhookList.some((webhook) => webhook.id === selectedWebhookId)) {
      setSelectedWebhookId(null);
    }
  }, [webhookList, selectedWebhookId]);

  // The limit can be reached from another session while the add form is open:
  // after a failed create the list is reloaded, the form can no longer be saved
  useEffect(() => {
    if (isLimitReached) {
      setIsAddFormOpen(false);
    }
  }, [isLimitReached]);

  // Open the editor of a just created webhook so its rules can be configured right away
  useEffect(() => {
    if (!pendingSelectUrl) return;
    const created = webhookList.find((webhook) => webhook.webhookUrl === pendingSelectUrl);
    if (created) {
      setSelectedWebhookId(created.id);
      setPendingSelectUrl(null);
    }
  }, [webhookList, pendingSelectUrl]);

  const handleToggleSelectWebhook = (webhookId) => {
    setSelectedWebhookId((prev) => (prev === webhookId ? null : webhookId));
    setIsAddFormOpen(false);
  };

  const handleOpenAddForm = () => {
    setSelectedWebhookId(null);
    setIsAddFormOpen(true);
  };

  const handleCreateWebhook = async (payload) => {
    const isSuccess = await createNewCustomerWebhookUrl(payload);

    if (isSuccess) {
      setIsAddFormOpen(false);
      setPendingSelectUrl(payload.webhookUrl);
    }

    return isSuccess;
  };

  const handleUpdateWebhook = async (payload) => {
    if (!selectedWebhook) return false;

    return await updateCustomerWebhookUrl(selectedWebhook.id, payload);
  };

  const handleConfirmDelete = async () => {
    if (!webhookToDelete) return;

    const isSuccess = await deleteCustomerWebhookUrl(webhookToDelete.id);

    if (isSuccess && selectedWebhookId === webhookToDelete.id) {
      setSelectedWebhookId(null);
    }

    setWebhookToDelete(null);
  };

  const handleSaveConfiguration = async (configuration) => {
    if (!selectedWebhook) return false;

    return await updateWebhookConfiguration(selectedWebhook.id, configuration);
  };

  if (!isInitialized) {
    return <Loader className={'application-loader webhook-tab-loader'} />;
  }

  return (
    <div className={'webhook-tab-container'}>
      {/* Direct API Call Token Section */}
      <Table responsive>
        <tbody className={'webhook-table-body'}>
          <tr className={'webhook-table-row'}>
            <td className={'webhook-table-cell'}>{i18n.getMessage('webhookTab.input.directApiCallToken.label')}</td>
            <td className={'webhook-table-cell'}>
              <div className={'table-input-wrapper'}>
                <span className={'table-cell-span'}>{directApiCallToken}</span>
                <div className={'webhook-actions-buttons-wrapper'}>
                  {!isDirectApiCallTokenExist ? (
                    <Button
                      className={'webhook-actions-button generate-button'}
                      roleType={'button'}
                      size={'small'}
                      type={'primary'}
                      onClick={generateDirectApiCallToken}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.generateToken')}
                    </Button>
                  ) : (
                    <Button
                      className={'webhook-actions-button generate-button'}
                      roleType={'button'}
                      size={'small'}
                      type={'primary'}
                      onClick={revokeDirectApiCallToken}
                      isDisabled={isLoading}
                    >
                      {i18n.getMessage('webhookTab.button.revokeToken')}
                    </Button>
                  )}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </Table>

      {/* Webhooks List Section */}
      <div className={'webhook-list-section'}>
        <div className={'webhook-list-header'}>
          <h3 className={'webhook-configuration-title webhook-list-title'}>
            {i18n.getMessage('webhookTab.list.title')}
            {Number.isInteger(webhookLimit) && (
              <span className={'webhook-list-counter'}>
                {webhookList.length}/{webhookLimit}
              </span>
            )}
          </h3>
          <Button
            className={'webhook-actions-button add-webhook-button'}
            roleType={'button'}
            size={'small'}
            type={'primary'}
            onClick={handleOpenAddForm}
            isDisabled={isLoading || isAddFormOpen || isLimitReached}
          >
            {i18n.getMessage('webhookTab.button.addWebhook')}
          </Button>
        </div>

        {isLimitReached && (
          <p className={'webhook-list-limit-hint'}>{i18n.getMessage('webhookTab.list.limitReached')}</p>
        )}

        {webhookList.length === 0 ? (
          <p className={'webhook-list-empty'}>{i18n.getMessage('webhookTab.list.empty')}</p>
        ) : (
          <Table responsive className={'webhook-list-table'}>
            <thead>
              <tr>
                {WEBHOOK_LIST_TABLE_HEADER.map((head) => (
                  <th key={head} className={'webhook-list-head'}>
                    {i18n.getMessage(`webhookTab.list.header.${head}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {webhookList.map((webhook) => {
                const isSelected = webhook.id === selectedWebhookId;
                return (
                  <tr key={webhook.id} className={`webhook-list-row ${isSelected ? 'selected' : ''}`}>
                    <td className={'webhook-list-cell webhook-list-url'}>{webhook.webhookUrl}</td>
                    <td className={'webhook-list-cell'}>
                      <span className={`webhook-direction-badge ${webhook.direction?.toLowerCase() || ''}`}>
                        {i18n.getMessage(`webhookTab.direction.${webhook.direction}`)}
                      </span>
                    </td>
                    <td className={'webhook-list-cell'}>{formatDateTime(webhook.createdAt)}</td>
                    <td className={'webhook-list-cell'}>
                      <div className={'webhook-actions-buttons-wrapper'}>
                        <Button
                          className={'webhook-actions-button other-buttons'}
                          roleType={'button'}
                          size={'small'}
                          type={isSelected ? 'outline' : 'primary'}
                          onClick={() => handleToggleSelectWebhook(webhook.id)}
                          isDisabled={isLoading}
                        >
                          {i18n.getMessage(isSelected ? 'webhookTab.button.close' : 'webhookTab.button.edit')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      {/* Add Webhook Form */}
      {isAddFormOpen && (
        <div className={'webhook-editor-section'}>
          <h3 className={'webhook-configuration-title'}>{i18n.getMessage('webhookTab.add.title')}</h3>
          <WebhookForm
            key={'new-webhook'}
            isLoading={isLoading}
            onSubmit={handleCreateWebhook}
            onCancel={() => setIsAddFormOpen(false)}
          />
        </div>
      )}

      {/* Selected Webhook Editor: key forces a fresh form/configuration state per webhook */}
      {selectedWebhook && (
        <div key={selectedWebhook.id} className={'webhook-editor-section'}>
          <h3 className={'webhook-configuration-title'}>
            {i18n.getMessage('webhookTab.edit.title')}
            <span className={'webhook-editor-url'}>{selectedWebhook.webhookUrl}</span>
          </h3>
          <WebhookForm
            webhook={selectedWebhook}
            isLoading={isLoading}
            onSubmit={handleUpdateWebhook}
            onDelete={() => setWebhookToDelete(selectedWebhook)}
            onTest={async (transactionType) => {
              await testWebhookUrl(selectedWebhook.id, transactionType);
              setHistoryReloadToken((token) => token + 1);
            }}
          />
          <WebhookConfiguration webhook={selectedWebhook} isLoading={isLoading} onSave={handleSaveConfiguration} />
        </div>
      )}

      <WebhookDeliveryHistory
        customerNumber={customerNumber}
        webhookList={webhookList}
        reloadToken={historyReloadToken}
      />

      <PopUp alignOnCenter show={!!webhookToDelete} onClose={() => setWebhookToDelete(null)}>
        {webhookToDelete && (
          <DeleteWebhookScheme
            webhookUrl={webhookToDelete.webhookUrl}
            isLoading={isLoading}
            handleConfirm={handleConfirmDelete}
            closePopUp={() => setWebhookToDelete(null)}
          />
        )}
      </PopUp>
    </div>
  );
};

WebhookTab.propTypes = {
  customerNumber: PropTypes.string,
  isLoading: PropTypes.bool,
  isInitialized: PropTypes.bool,
  getCurrentCustomerWebhookUrlList: PropTypes.func,
  createNewCustomerWebhookUrl: PropTypes.func,
  updateCustomerWebhookUrl: PropTypes.func,
  deleteCustomerWebhookUrl: PropTypes.func,
  testWebhookUrl: PropTypes.func,
  updateWebhookConfiguration: PropTypes.func,
  customerWebhookUrlList: PropTypes.array,
  directApiCallToken: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  generateDirectApiCallToken: PropTypes.func,
  revokeDirectApiCallToken: PropTypes.func,
  checkDirectApiCallToken: PropTypes.func,
  isDirectApiCallTokenExist: PropTypes.bool
};

export default inject(() => ({}))(observer(WebhookTab));
